import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import ThemeToggle from '@/components/ThemeToggle';

const risks = [
  {
    icon: '⏳',
    title: 'Tempi variabili',
    desc: 'Il completamento di un City-Box dipende dalla partecipazione della comunità. Non esiste una scadenza garantita.',
  },
  {
    icon: '🚫',
    title: 'Nessuna garanzia di rendimento',
    desc: 'Questo NON è un investimento finanziario. Non promettiamo alcun ROI. Il payout dipende dal completamento del box.',
  },
  {
    icon: '🔄',
    title: 'Rischio di stallo',
    desc: 'Se un box non si completa, il fondo di stabilizzazione interviene. Nei casi estremi, i box possono essere fusi.',
  },
  {
    icon: '💸',
    title: 'Donazione non rimborsabile',
    desc: 'La donazione iniziale non è rimborsabile. Si tratta di un contributo al ciclo comunitario del dono.',
  },
];

const rules = [
  { label: 'Membri per City-Box', value: '11', detail: '1 Receiver + 2 Bridge + 8 Nuovi Donatori' },
  { label: 'Donazione unitaria', value: '€100', detail: 'Importo fisso configurabile dall\'admin' },
  { label: 'Payout al Receiver', value: '80%', detail: '€800 su €1.000 raccolti (8 donazioni × €100 + 2 Bridge)' },
  { label: 'Fondo stabilizzazione', value: '10%', detail: '€100 per assorbire stalli e box incompleti' },
  { label: 'Fee operativa', value: '10%', detail: '€100 per costi piattaforma, manutenzione, sviluppo' },
  { label: 'Max box per utente', value: '3', detail: 'Policy anti-espansione: nessuno può monopolizzare' },
];

const phases = [
  {
    step: 1,
    emoji: '�️',
    role: 'Nuovo Donatore',
    color: 'primary',
    title: 'Donazione e ingresso',
    desc: 'Entri in un City-Box aperto donando €100. Vieni posizionato nel box con più posti disponibili (matching engine best-fit).',
  },
  {
    step: 2,
    emoji: '🌉',
    role: 'Bridge',
    color: 'accent',
    title: 'Accompagnamento',
    desc: 'Quando un box si completa e si sdoppia, i 2 Bridge diventano Receiver nei 2 nuovi box. Guidi i nuovi donatori.',
  },
  {
    step: 3,
    emoji: '💎',
    role: 'Receiver',
    color: 'blue',
    title: 'Ricezione del payout',
    desc: 'Quando il tuo box si completa (11/11 slot pieni), ricevi l\'80% del totale raccolto: €800.',
  },
  {
    step: 4,
    emoji: '♻️',
    role: 'Scelta',
    color: 'surface',
    title: 'Reinvesti o esci',
    desc: 'Puoi reinvestire €100 e rientrare come Nuovo Donatore (ciclo perpetuo) oppure uscire dal movimento.',
  },
];

export default function HowItWorksPage() {
  return (
    <>
      <Helmet>
        <title>Come funziona — React-City</title>
        <meta
          name="description"
          content="Scopri come funziona il Movimento del Dono. Regole, ruoli, rischi e trasparenza totale."
        />
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-[#1b1b1f] transition-colors">
        {/* Theme Toggle */}
        <div className="fixed top-4 right-4 z-50">
          <ThemeToggle />
        </div>

        {/* Hero */}
        <section className="bg-hero-gradient py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="text-5xl mb-4 block">📖</span>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 text-balance">
              Come funziona il Movimento del Dono
            </h1>
            <p className="text-lg text-white/80 dark:text-surface-300 max-w-2xl mx-auto">
              Trasparenza totale. Regole chiare. Nessuna promessa di rendimento.
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
          <div className="card border-l-4 border-l-accent-500 dark:border-l-accent-400">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p className="font-semibold text-heading text-sm">Avviso importante</p>
                <p className="text-xs text-muted mt-1">
                  Il Movimento del Dono è un sistema di <strong className="text-heading">mutuo sostegno comunitario</strong>.
                  Non è un investimento finanziario, non è uno schema piramidale, e non garantisce alcun rendimento.
                  Ogni partecipante dona consapevolmente a beneficio della comunità.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Il percorso */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-heading mb-8 text-center">Il percorso nel City-Box</h2>
            <div className="space-y-6">
              {phases.map((phase) => (
                <div key={phase.step} className="card flex items-start gap-4 sm:gap-6 animate-in">
                  <div className={`flex-shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center text-2xl
                    bg-${phase.color}-100 dark:bg-${phase.color}-500/15`}>
                    {phase.emoji}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`badge-${phase.color === 'primary' ? 'green' : phase.color === 'accent' ? 'yellow' : phase.color === 'blue' ? 'blue' : 'gray'}`}>
                        Fase {phase.step}
                      </span>
                      <span className="text-xs text-muted">{phase.role}</span>
                    </div>
                    <h3 className="font-semibold text-heading mb-1">{phase.title}</h3>
                    <p className="text-sm text-muted">{phase.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Regole */}
        <section className="py-16 bg-surface-50 dark:bg-[#1e1e22]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-heading mb-8 text-center">Regole del sistema</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {rules.map((rule) => (
                <div key={rule.label} className="card-flat">
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-sm font-medium text-heading">{rule.label}</span>
                    <span className="text-lg font-bold text-primary-500">{rule.value}</span>
                  </div>
                  <p className="text-xs text-muted">{rule.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Distribuzione visuale */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-heading mb-8 text-center">Distribuzione del dono</h2>
            <div className="card">
              <div className="space-y-4">
                {/* Receiver */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-heading font-medium">💎 Receiver (payout)</span>
                    <span className="text-primary-500 font-bold">80% — €800</span>
                  </div>
                  <div className="h-4 rounded-full bg-surface-100 dark:bg-surface-800 overflow-hidden">
                    <div className="h-full rounded-full bg-primary-500 dark:bg-primary-400 transition-all duration-1000"
                      style={{ width: '80%' }} />
                  </div>
                </div>
                {/* Fondo */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-heading font-medium">🛡️ Fondo stabilizzazione</span>
                    <span className="text-accent-500 font-bold">10% — €100</span>
                  </div>
                  <div className="h-4 rounded-full bg-surface-100 dark:bg-surface-800 overflow-hidden">
                    <div className="h-full rounded-full bg-accent-500 dark:bg-accent-400 transition-all duration-1000"
                      style={{ width: '10%' }} />
                  </div>
                </div>
                {/* Fee */}
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-heading font-medium">⚙️ Fee operativa</span>
                    <span className="text-surface-400 font-bold">10% — €100</span>
                  </div>
                  <div className="h-4 rounded-full bg-surface-100 dark:bg-surface-800 overflow-hidden">
                    <div className="h-full rounded-full bg-surface-400 dark:bg-surface-600 transition-all duration-1000"
                      style={{ width: '10%' }} />
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted mt-4 text-center">
                Totale raccolto per box: 10 donazioni × €100 = €1.000 (1 Receiver non dona, 2 Bridge donano nel ciclo precedente)
              </p>
            </div>
          </div>
        </section>

        {/* Rischi */}
        <section className="py-16 bg-surface-50 dark:bg-[#1e1e22]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-heading mb-3 text-center">Rischi e limitazioni</h2>
            <p className="text-center text-muted text-sm mb-8 max-w-lg mx-auto">
              La trasparenza è un valore fondamentale. Ecco i rischi di cui devi essere consapevole.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {risks.map((risk) => (
                <div key={risk.title} className="card border-l-4 border-l-red-400 dark:border-l-red-500">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{risk.icon}</span>
                    <div>
                      <p className="font-medium text-heading text-sm">{risk.title}</p>
                      <p className="text-xs text-muted mt-1">{risk.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Meccanismi di protezione */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-heading mb-8 text-center">Meccanismi di protezione</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="card text-center">
                <span className="text-3xl mb-3 block">🛡️</span>
                <h3 className="font-semibold text-heading text-sm mb-2">Fondo di stabilizzazione</h3>
                <p className="text-xs text-muted">
                  Il 10% di ogni donazione alimenta un fondo che compensa stalli e box incompleti.
                </p>
              </div>
              <div className="card text-center">
                <span className="text-3xl mb-3 block">🔀</span>
                <h3 className="font-semibold text-heading text-sm mb-2">Merge automatico</h3>
                <p className="text-xs text-muted">
                  I box incompleti dopo un timeout vengono fusi in box attivi per evitare blocchi permanenti.
                </p>
              </div>
              <div className="card text-center">
                <span className="text-3xl mb-3 block">📏</span>
                <h3 className="font-semibold text-heading text-sm mb-2">Policy anti-espansione</h3>
                <p className="text-xs text-muted">
                  Limite max box per utente: nessuno può monopolizzare il sistema.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Pronto a entrare nel movimento?</h2>
            <p className="text-white/80 dark:text-surface-300 mb-8 max-w-lg mx-auto text-sm">
              Hai letto le regole, conosci i rischi. Se condividi i valori del mutuo sostegno, il tuo posto ti aspetta.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="btn-lg bg-white text-primary-700 hover:bg-primary-50 dark:bg-primary-500 dark:text-white dark:hover:bg-primary-400 font-bold rounded-2xl px-8 py-4 shadow-lg no-underline"
              >
                Registrati ora
              </Link>
              <Link
                to="/"
                className="btn-lg bg-white/10 text-white hover:bg-white/20 border border-white/30 dark:border-surface-600 rounded-2xl px-8 py-4 no-underline"
              >
                Torna alla home
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-10 bg-surface-900 dark:bg-[#141416] text-surface-400">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4 text-sm">
              <Link to="/privacy" className="text-surface-400 hover:text-white transition-colors no-underline">
                Privacy Policy
              </Link>
              <span className="hidden sm:inline text-surface-600">·</span>
              <Link to="/cookie-policy" className="text-surface-400 hover:text-white transition-colors no-underline">
                Cookie Policy
              </Link>
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} React-City — Movimento del Dono.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
